import React, { useEffect, useState } from "react";
import { Plus, Loader2, Trash2, KeyRound, ShieldCheck, Headset } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../../components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Switch } from "../../components/ui/switch";
import { Field, inputCls } from "./fields";

const RoleBadge = ({ role }) =>
  role === "admin" ? (
    <span className="inline-flex items-center gap-1 rounded-full bg-[#0f2e2a] px-2.5 py-1 text-[12px] font-semibold text-white"><ShieldCheck size={12} />Main admin</span>
  ) : (
    <span className="inline-flex items-center gap-1 rounded-full bg-[#e0f5f0] px-2.5 py-1 text-[12px] font-semibold text-[#0b6f65]"><Headset size={12} />Agent</span>
  );

const AgentsTab = ({ me }) => {
  const [items, setItems] = useState(null);
  const [form, setForm] = useState(null);
  const [pw, setPw] = useState(null);
  const [del, setDel] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/agents").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const err = (e) => toast.error(e?.response?.data?.detail || "Something went wrong");

  const create = async () => {
    setBusy(true);
    try {
      await api.post("/admin/agents", form);
      toast.success(`${form.name} can now sign in at /admin`);
      setForm(null);
      load();
    } catch (e) { err(e); } finally { setBusy(false); }
  };

  const update = async (email, body, msg) => {
    try {
      await api.put(`/admin/agents/${encodeURIComponent(email)}`, body);
      if (msg) toast.success(msg);
      load();
    } catch (e) { err(e); }
  };

  const savePw = async () => {
    await update(pw.email, { password: pw.password }, "Password updated");
    setPw(null);
  };

  const remove = async () => {
    try {
      await api.delete(`/admin/agents/${encodeURIComponent(del.email)}`);
      toast.success("Account removed");
      setDel(null);
      load();
    } catch (e) { err(e); }
  };

  if (!items) return <Loader2 className="animate-spin text-[#0e8c7f]" />;

  return (
    <div data-testid="agents-tab">
      <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="max-w-xl text-[14.5px] text-[#5b6d6a]"><b>Agents</b> can answer live chats and manage quote requests. Only the <b>main admin</b> can edit products, website content, alerts and team accounts.</p>
        <button onClick={() => setForm({ name: "", email: "", password: "", role: "agent" })} className="sw-btn-primary !py-2.5 shrink-0" data-testid="add-agent-btn"><Plus size={17} />Add team member</button>
      </div>

      <div className="overflow-hidden rounded-2xl border border-[#e3f0ed] bg-white">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[680px] text-left text-[14.5px]">
            <thead className="bg-[#f4fbf9] text-[12.5px] uppercase tracking-wider text-[#5b6d6a]">
              <tr><th className="px-5 py-3">Name</th><th className="px-5 py-3">Email</th><th className="px-5 py-3">Role</th><th className="px-5 py-3">Active</th><th className="px-5 py-3 text-right">Actions</th></tr>
            </thead>
            <tbody>
              {items.map((a) => {
                const self = a.email === me.email;
                return (
                  <tr key={a.email} className="border-t border-[#eef5f3]" data-testid="agent-row">
                    <td className="px-5 py-3 font-semibold text-[#0f2e2a]">{a.name}{self && <span className="ml-2 text-[12px] font-normal text-[#8a9a97]">(you)</span>}</td>
                    <td className="px-5 py-3 text-[#4b5d5a]">{a.email}</td>
                    <td className="px-5 py-3">
                      {self ? <RoleBadge role={a.role} /> : (
                        <Select value={a.role} onValueChange={(v) => update(a.email, { role: v }, "Role updated")}>
                          <SelectTrigger className="h-9 w-[150px] rounded-full"><SelectValue /></SelectTrigger>
                          <SelectContent><SelectItem value="agent">Agent</SelectItem><SelectItem value="admin">Main admin</SelectItem></SelectContent>
                        </Select>
                      )}
                    </td>
                    <td className="px-5 py-3"><Switch checked={!a.disabled} disabled={self} onCheckedChange={(v) => update(a.email, { disabled: !v }, v ? "Account enabled" : "Account disabled")} /></td>
                    <td className="px-5 py-3 text-right">
                      <button onClick={() => setPw({ email: a.email, name: a.name, password: "" })} className="rounded-lg p-2 text-[#0e8c7f] hover:bg-[#e8f8f4]" title="Reset password" aria-label="Reset password"><KeyRound size={16} /></button>
                      {!self && <button onClick={() => setDel(a)} className="rounded-lg p-2 text-red-500 hover:bg-red-50" aria-label="Delete" data-testid="delete-agent-btn"><Trash2 size={16} /></button>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="max-w-[480px]">
          <DialogHeader><DialogTitle>Add team member</DialogTitle></DialogHeader>
          {form && (
            <div className="space-y-4">
              <Field label="Name"><input className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="agent-name-input" /></Field>
              <Field label="Login email"><input type="email" className={inputCls} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="agent-email-input" /></Field>
              <Field label="Temporary password (min 6)"><input className={inputCls} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="agent-password-input" /></Field>
              <Field label="Role">
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="agent">Agent — chats & quotes only</SelectItem><SelectItem value="admin">Main admin — full access</SelectItem></SelectContent>
                </Select>
              </Field>
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setForm(null)} className="sw-btn-outline !py-2">Cancel</button>
            <button onClick={create} disabled={busy} className="sw-btn-primary !py-2.5 disabled:opacity-60" data-testid="save-agent-btn">{busy && <Loader2 size={16} className="animate-spin" />}Create account</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!pw} onOpenChange={(o) => !o && setPw(null)}>
        <DialogContent className="max-w-[420px]">
          <DialogHeader><DialogTitle>Reset password for {pw?.name}</DialogTitle></DialogHeader>
          <Field label="New password (min 6)"><input className={inputCls} value={pw?.password || ""} onChange={(e) => setPw({ ...pw, password: e.target.value })} /></Field>
          <DialogFooter>
            <button onClick={() => setPw(null)} className="sw-btn-outline !py-2">Cancel</button>
            <button onClick={savePw} className="sw-btn-primary !py-2.5">Save password</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!del} onOpenChange={(o) => !o && setDel(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {del?.name}?</AlertDialogTitle>
            <AlertDialogDescription>They will no longer be able to sign in. Their past chat replies stay in the inbox.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-red-600 hover:bg-red-700">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AgentsTab;
